import React, { useState, useMemo } from 'react';
import {
  Box, Button, Popover, Typography, TextField, Divider, List, ListItemButton,
  ListItemText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const isoDate = (d) => {
  if (typeof d === 'string') return d;
  return d.toISOString().slice(0, 10);
};

const formatRangeLabel = (start, end) => {
  if (!start || !end) return 'Select range';
  const s = new Date(start);
  const e = new Date(end);
  const sameYear = s.getFullYear() === e.getFullYear();
  const sameMonth = sameYear && s.getMonth() === e.getMonth();
  const sameDay = sameMonth && s.getDate() === e.getDate();
  if (sameDay) {
    return s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (sameYear) {
    return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} - ${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

// Build presets relative to "today"
const buildPresets = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Sunday-start week
  const sundayThisWeek = new Date(today);
  sundayThisWeek.setDate(today.getDate() - today.getDay());

  const last7Start = new Date(today);
  last7Start.setDate(today.getDate() - 6);

  const lastSundayStart = new Date(sundayThisWeek);
  lastSundayStart.setDate(sundayThisWeek.getDate() - 7);
  const lastSaturdayEnd = new Date(sundayThisWeek);
  lastSaturdayEnd.setDate(sundayThisWeek.getDate() - 1);

  const last28Start = new Date(today);
  last28Start.setDate(today.getDate() - 27);

  const last30Start = new Date(today);
  last30Start.setDate(today.getDate() - 29);

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  const last90Start = new Date(today);
  last90Start.setDate(today.getDate() - 89);

  const quarterMonth = Math.floor(today.getMonth() / 3) * 3;
  const quarterStart = new Date(today.getFullYear(), quarterMonth, 1);

  const thisYearStart = new Date(today.getFullYear(), 0, 1);
  const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
  const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);

  return [
    { key: 'custom', label: 'Custom', start: null, end: null },
    { key: 'today', label: 'Today', start: today, end: today },
    { key: 'yesterday', label: 'Yesterday', start: yesterday, end: yesterday },
    { key: 'this_week', label: 'This week (Sun - Today)', start: sundayThisWeek, end: today },
    { key: 'last_7_days', label: 'Last 7 days', start: last7Start, end: today },
    { key: 'last_week', label: 'Last week (Sun - Sat)', start: lastSundayStart, end: lastSaturdayEnd },
    { key: 'last_28_days', label: 'Last 28 days', start: last28Start, end: today },
    { key: 'last_30_days', label: 'Last 30 days', start: last30Start, end: today },
    { key: 'this_month', label: 'This month', start: thisMonthStart, end: today },
    { key: 'last_month', label: 'Last month', start: lastMonthStart, end: lastMonthEnd },
    { key: 'last_90_days', label: 'Last 90 days', start: last90Start, end: today },
    { key: 'quarter_to_date', label: 'Quarter to date', start: quarterStart, end: today },
    { key: 'this_year', label: 'This year (Jan - Today)', start: thisYearStart, end: today },
    { key: 'last_calendar_year', label: 'Last calendar year', start: lastYearStart, end: lastYearEnd },
  ];
};

/**
 * Reusable date range picker with Google-Analytics-style presets
 * Props:
 *   value: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }
 *   onChange: ({ start, end, presetKey }) => void
 *   defaultPresetLabel?: string
 *   size?: 'small' | 'medium'
 */
export default function DateRangePicker({ value, onChange, defaultPresetLabel = 'Custom', size = 'small' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tempStart, setTempStart] = useState(value?.start || '');
  const [tempEnd, setTempEnd] = useState(value?.end || '');
  const [activePreset, setActivePreset] = useState('custom');

  const presets = useMemo(() => buildPresets(), []);

  const handleOpen = (e) => {
    setTempStart(value?.start || '');
    setTempEnd(value?.end || '');
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handlePresetClick = (preset) => {
    setActivePreset(preset.key);
    if (preset.key === 'custom') return;
    setTempStart(isoDate(preset.start));
    setTempEnd(isoDate(preset.end));
  };

  const handleApply = () => {
    if (!tempStart || !tempEnd) return;
    onChange?.({ start: tempStart, end: tempEnd, presetKey: activePreset });
    handleClose();
  };

  const open = Boolean(anchorEl);
  const label = value?.start && value?.end
    ? formatRangeLabel(value.start, value.end)
    : defaultPresetLabel;

  // Find which preset matches current value
  const currentPresetLabel = useMemo(() => {
    if (!value?.start || !value?.end) return defaultPresetLabel;
    const match = presets.find((p) => {
      if (!p.start || !p.end) return false;
      return isoDate(p.start) === value.start && isoDate(p.end) === value.end;
    });
    return match && match.key !== 'custom' ? match.label : 'Custom';
  }, [value, presets, defaultPresetLabel]);

  return (
    <>
      <Button
        variant="outlined" size={size} onClick={handleOpen}
        startIcon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{
          borderRadius: 2, textTransform: 'none', fontWeight: 600,
          color: PRIMARY, borderColor: '#C4CDD5',
          backgroundColor: 'white',
          '&:hover': { borderColor: PRIMARY, backgroundColor: '#F5F7FA' },
          minWidth: 240, justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {currentPresetLabel}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {label}
          </Typography>
        </Box>
      </Button>

      <Popover
        open={open} anchorEl={anchorEl} onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', mt: 0.5 },
        }}
      >
        <Box sx={{ display: 'flex', minWidth: 600 }}>
          {/* Left: presets */}
          <Box sx={{ width: 220, borderRight: '1px solid #E0E0E0', maxHeight: 460, overflowY: 'auto' }}>
            <List dense disablePadding>
              {presets.map((p) => (
                <ListItemButton
                  key={p.key}
                  selected={activePreset === p.key}
                  onClick={() => handlePresetClick(p)}
                  sx={{
                    py: 1, px: 2,
                    '&.Mui-selected': {
                      backgroundColor: '#E3F2FD',
                      borderLeft: `3px solid ${PRIMARY}`,
                      '&:hover': { backgroundColor: '#BBDEFB' },
                    },
                  }}
                >
                  <ListItemText
                    primary={p.label}
                    primaryTypographyProps={{
                      fontSize: 13,
                      fontWeight: activePreset === p.key ? 700 : 500,
                      color: activePreset === p.key ? PRIMARY : 'text.primary',
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>

          {/* Right: date inputs + actions */}
          <Box sx={{ flex: 1, p: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth size="small" type="date" label="Start date"
                value={tempStart}
                onChange={(e) => { setTempStart(e.target.value); setActivePreset('custom'); }}
                InputLabelProps={{ shrink: true }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>—</Box>
              <TextField
                fullWidth size="small" type="date" label="End date"
                value={tempEnd}
                onChange={(e) => { setTempEnd(e.target.value); setActivePreset('custom'); }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Tip: Pick a preset on the left or set custom dates above.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={handleClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                Cancel
              </Button>
              <Button
                variant="contained" onClick={handleApply}
                disabled={!tempStart || !tempEnd}
                sx={{
                  borderRadius: 2, textTransform: 'none', fontWeight: 600,
                  backgroundColor: PRIMARY, '&:hover': { backgroundColor: '#142847' },
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
